import { EventParticipation, EventVisibility, ParticipationStatus } from '@seiri/shared/common'
import { matchesPerson } from './person-search'

/**
 * The statuses a manager sees, in the order the status filter lists them. `cancelled` shows only sign-ups cancelled
 * since 2026-10-15 (`cancelledAt`); the rows the old "Odobrať" left behind stay hidden.
 */
export const VISIBLE_PARTICIPATION_STATUSES = [
  'confirmed',
  'waiting',
  'requested',
  'invited',
  'denied',
  'cancelled',
] as const
export type VisibleParticipationStatus = (typeof VISIBLE_PARTICIPATION_STATUSES)[number]

/** Labels for the status filter on the event detail page. */
export const PARTICIPATION_STATUS_LABELS: Record<VisibleParticipationStatus, string> = {
  confirmed: 'Prihlásení',
  waiting: 'Čakacia listina',
  requested: 'Čaká na schválenie',
  invited: 'Pozvaní',
  denied: 'Zamietnutí',
  cancelled: 'Zrušení',
}

const time = (value: Date | string | null) => (value === null ? 0 : new Date(value).getTime())

/**
 * An event's participations by status. The waiting list is ordered by when each joined it, the
 * cancelled by when they were cancelled, every other status by sign-up. A `cancelled` row without
 * `cancelledAt` - left behind by the old "Odobrať" - is dropped.
 */
export function groupParticipationsByStatus(
  participations: readonly EventParticipation[],
): Record<VisibleParticipationStatus, EventParticipation[]> {
  const groups: Record<VisibleParticipationStatus, EventParticipation[]> = {
    confirmed: [],
    waiting: [],
    requested: [],
    invited: [],
    denied: [],
    cancelled: [],
  }
  for (const participation of participations) {
    const status: ParticipationStatus = participation.status
    if (status !== 'cancelled' || participation.cancelledAt) {
      groups[status].push(participation)
    }
  }
  const sortKey = { waiting: 'waiting', cancelled: 'cancelledAt' } as const
  for (const status of VISIBLE_PARTICIPATION_STATUSES) {
    const key = status === 'waiting' || status === 'cancelled' ? sortKey[status] : 'createdAt'
    groups[status].sort((a, b) => time(a[key]) - time(b[key]))
  }
  return groups
}

/** The search on the participant list: name or e-mail ignoring case and diacritics, or phone ignoring spaces. */
export function matchesParticipant(participation: EventParticipation, query: string): boolean {
  return matchesPerson(participation.customer, query)
}

/** Who can see an event, as the create summaries (one value) and the detail page (the stored list) word it. */
export function eventVisibilityLabel(
  visibility: EventVisibility | readonly EventVisibility[] | null | undefined,
): string {
  const values: readonly EventVisibility[] = Array.isArray(visibility) ? visibility : visibility ? [visibility] : []
  if (values.includes(EventVisibility.PUBLIC)) return 'Verejnosť'
  // On no list of the public page; anyone with the termín's link can sign up (docs/events.md, _Visibility_).
  if (values.includes(EventVisibility.UNLISTED)) return 'Nevypísaný'
  return 'Iba správca'
}
